/**
 * Inline citations.
 *
 * The runtime numbers an answer's citations [1], [2], ... in the order of
 * `metadata.retrievedContext`, so [n] in the text is the nth source listed
 * under the answer. These helpers turn each [n] into a link the markdown
 * renderer shows as a superscript that opens that source.
 */

export const CITATION_HREF_PREFIX = '#citation-';

const CODE_SPAN_PATTERN = /(```[\s\S]*?```|`[^`\n]*`)/g;
// [n] not followed by "(": "[1](https://…)" is a markdown link, not a citation.
const CITATION_PATTERN = /\[(\d+)\](?!\()/g;

/**
 * Rewrite [n] as a markdown link to citation n, for n within the source list.
 * A number past the list (a footnote the agent wrote itself, a range like
 * [2024]) and anything inside code are left as written.
 */
export function linkCitations(text, count) {
  if (!text || typeof text !== 'string' || !count) {
    return text;
  }
  return text
    .split(CODE_SPAN_PATTERN)
    .map(segment => {
      if (segment.startsWith('`')) {
        return segment;
      }
      return segment.replace(CITATION_PATTERN, (match, digits) => {
        const number = Number(digits);
        return number >= 1 && number <= count ? `[${number}](${CITATION_HREF_PREFIX}${number})` : match;
      });
    })
    .join('');
}

/** The citation number a link points to, or null for any other link. */
export function citationNumber(href) {
  if (typeof href !== 'string' || !href.startsWith(CITATION_HREF_PREFIX)) {
    return null;
  }
  const number = Number(href.slice(CITATION_HREF_PREFIX.length));
  return Number.isInteger(number) && number >= 1 ? number : null;
}

/** "p. 5" / "pp. 2, 5", or null when the source has no pages (only PDFs do). */
export function pageLabel(entry) {
  const pages = Array.isArray(entry?.pages) ? entry.pages : [];
  if (pages.length === 0) {
    return null;
  }
  return `${pages.length === 1 ? 'p.' : 'pp.'} ${pages.join(', ')}`;
}

/** The document as the reader knows it: file name, else the uploader's id. */
export function sourceName(entry) {
  return entry?.title || entry?.sourceId || 'unknown source';
}

/** One line naming a source, for a citation's tooltip. */
export function citationTitle(entry) {
  if (!entry) {
    return undefined;
  }
  const page = pageLabel(entry);
  return page ? `${sourceName(entry)}, ${page}` : sourceName(entry);
}
